import { LitElement, html, css } from "lit";
import {property, customElement} from 'lit/decorators.js';
import {
    FiSubView,
    UiStateModel,
    UiStateModelParam,
} from "../../../src/models/ui-state-model";
import { voyageCursorStyles } from "../../voyage-style/src/voyage-style";
import { SvgIcons } from "../../voyage-svg-icons/src/voyage-svg-icons";
import { VoyageShoreContextModel } from "../../../src/models/voyage-shore-context-model";

import "../../voy-fleet-alarms/src/voy-fleet-alarm"
import "../../voyage-spinner/src/voyage-spinner"
import { VesselInfo } from "../../../src/models/types/vessels-model.types";
import { FleetAlarm, FleetAlarmLog } from "../../../src/models/types/voy-fleet-alarm-model.types";
import { VoyFleetAlarmModel } from "../../../src/models/voy-fleet-alarm-model";
import { AlarmType } from "../../../src/services/types/fleet-alarms-service.types";
import { VesselsModel } from "../../../src/models/vessels-model";
import { VesselVoyageVersion } from "../../voy-global-filters/src/types";

@customElement("voy-fleet-sub-view-alarm-details")
export class VoyFleetSubViewAlarmDetails extends LitElement {
    @property({ type: Number }) public imo?: number;
    @property({ type: Object, attribute: false}) private _alarm: FleetAlarm | null = null;
    @property({ attribute: false }) private _handleAlarmPopupShow: boolean = false;
    @property({ attribute: false }) private _alarmType: AlarmType | null = null;
    @property({ attribute: false }) private _maximized: boolean = false;
    @property({ type: Object, attribute: false }) private _vessel?: VesselInfo;

    constructor() {
        super();
            this._goToSubView = this._goToSubView.bind(this);
            this._setAlarm = this._setAlarm.bind(this);
            this._toggleAlarmPopup = this._toggleAlarmPopup.bind(this);
            this._handleAlarm = this._handleAlarm.bind(this);
            this._setMaximized = this._setMaximized.bind(this)
            this._setVessel = this._setVessel.bind(this);
    }

    static styles = [
        voyageCursorStyles,
        css`
        :host {
            display: block;
            font-size: 12px;
            line-height: 16px;
            font-family: "MaerskText";
            overflow-x: hidden;
        }
        header {
            display: flex;
            flex-direction: row;
            column-gap: 4px;
            padding: 24px 16px;
            border-bottom: 1px solid var(--neutrals-grey-06);
            box-shadow: var(--voyage-box-shadow) 0px 6px 10px;
        }
        header h1 {
            margin: 0;
            font-size: 26px;
            line-height: 32px;
            font-weight: 400;
            font-family: MaerskHeadline;
            color: var(--neutrals-grey-02);
        }
        header button {
            background: none;
            border: none;
            margin-top: 6px;
            cursor: pointer;
            padding: 0;
            margin-left: -6px;
            display: flex;
            align-items: flex-start;
        }
        
        nav {
            padding: 16px;
            flex-direction: row;
            flex-wrap: wrap;
            align-items: center;
            border-top: 1px solid var(--neutrals-grey-06);
            border-bottom: 1px solid var(--neutrals-grey-06);
            box-shadow: var(--voyage-box-shadow) 0px 6px 10px;
            position: relative;
            z-index: 1;
        }
        nav button {
            font-family: "MaerskText";
            display: flex;
            column-gap: 8px;
            align-items: center;
            background: none;
            color: var(--voyage-blue-dark);
            border: none;
            flex: 1 0 50%;
            text-align: left;
            cursor: pointer;
        }

        .container {
            background-color: var(--neutrals-grey-10);
            overflow-y: scroll;
            position: relative;
            margin-top: 10px;
            overflow-x: hidden;
            max-height: calc(100vh - 287px);
           }
        .container .toggle-chart {
            top: 16px;
            right: 16px;
            position: absolute;
            cursor: pointer;
        }
        .container .toggle-chart svg {
            width: 16px;
            height: 16px;
        }

        .container[maximized] {
            width: calc(100vw - 86px);
            position: fixed;
            bottom: 56px;
            box-shadow: var(--voyage-box-shadow) 0px 6px 10px;
            z-index: 2;
        }

        .spinner-wrapper {
            position: relative;
            height: 100px;
            width: 100%;
            background-color: var(--neutrals-grey-10);
        }
        voyage-spinner {
            width: 50px;
            height: 50px;
            left: calc(50% - 25px);
            top: calc(50% - 25px);
            position: absolute;
        }
        .premium-icon svg{
                height: 16px;
                width: 16px;
        }
    `];

    render() {
        const isPremium:boolean = this._vessel?.vesselVersion === VesselVoyageVersion.Premium;
        return html`
            <header class="header-navigation">
                <button @click=${() => this._goToSubView(FiSubView.VesselAlarmsLog)} data-test="back-button">${SvgIcons.customChevronLeft}</button>
                <h1><span class="premium-icon" ?hidden=${!isPremium}>${SvgIcons.customPremiumVessel}</span>${this._vessel?.vesselName || "N/A"}<br>Alarm Log</h1>
            </header>
            ${this._alarm && this.imo ? 
                html `
                    <div class="container" ?maximized=${this._maximized}>
                        <span class="toggle-chart" title="${this._maximized ? 'Minimize performance charts' : 'Maximize performance charts'}" @click=${this._setMaximized}>
                            ${this._maximized ? SvgIcons.solidCompressArrowsAlt : SvgIcons.expandPageIcon}
                        </span>
                        <voy-fleet-alarm 
                            .maximized=${this._maximized}
                            .alarm=${this._alarm} 
                            .imo=${this.imo}
                            @handle-alarm=${this._toggleAlarmPopup}>
                        </voy-fleet-alarm>
                        <voy-handle-alarm-popup 
                            .alarmEventType=${this._alarmType} 
                            ?shown=${this._handleAlarmPopupShow}
                            @close=${this._handleAlarm}>
                        </voy-handle-alarm-popup> 
                    </div>`
                : html`
                    <div class="spinner-wrapper">
                        <div class="loadingOverlay">
                            <voyage-spinner></voyage-spinner>
                        </div>
                    </div>
                `
            }
        `
    }

    firstUpdated() {
        this._setAlarm();
        this._setVessel();
    }

    connectedCallback() {
        super.connectedCallback();

        UiStateModel!.addEventListener(`${UiStateModelParam.IMO}-change`, this._setVessel);
        VoyFleetAlarmModel!.addEventListener('alarms-updated', this._setAlarm);
    }

    disconnectedCallback() {
        super.disconnectedCallback();

        UiStateModel!.removeEventListener(`${UiStateModelParam.IMO}-change`, this._setVessel);
        VoyFleetAlarmModel!.removeEventListener('alarms-updated', this._setAlarm);
    }

    private _goToSubView(view: FiSubView) {
        UiStateModel!.setValue(UiStateModelParam.FISubView, view);
    }

    private async _setAlarm() {
        const selectedAlarmId = UiStateModel!.getValue(UiStateModelParam.VesselAlarmId);

        if (!selectedAlarmId) return;
        const alarm = await VoyFleetAlarmModel!.getVesselAlarmByID(parseInt(selectedAlarmId))

        if (!alarm) return
        const sortedLogs: FleetAlarmLog[] = alarm.alarmLogs.sort((a, b) => {
            return new Date(b.createdAt).valueOf() - new Date(a.createdAt).valueOf()
        });

        this._alarm = {...alarm, alarmLogs: sortedLogs }
    }

    private _toggleAlarmPopup() {
        if (!this._alarm) return

        this._alarmType = this._alarm!.alarmType
        this._handleAlarmPopupShow = !this._handleAlarmPopupShow;
    }

    private async _handleAlarm(e: CustomEvent){
        // If the action dispatched from the pop handler us cancel
        // don't run the update alarm function
        if (!e.detail.action) {
            this._toggleAlarmPopup();
            return;
        } 

        const alarm = await VoyFleetAlarmModel?.updateAlarm(this._alarm!.id, e.detail.action, e.detail.selectedReason, e.detail.alarmNote);
        this._toggleAlarmPopup();
        
        if (!alarm) return;

        this._alarm = alarm;
        this.requestUpdate();
    }

    private _setMaximized() {
        this._maximized = !this._maximized;
    }

    private async _setVessel(): Promise<void> {
        if (!this.imo) return;
        this._vessel = VoyageShoreContextModel!.vessels.find(v => v.imoNo === this.imo);
        if(!this._vessel){
            await VesselsModel?.refreshVessels()
            this._vessel = VesselsModel!.getVessel(this.imo)
        }
    }
}
